import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material'
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { LoadingSpinner } from '../../../../../presentation-components/display-elements-components/loading-spinner'
import { TableDisplay } from '../../../../../presentation-components/miscellaneous-components/table-display'
import { ValidationResultsStepCard } from '../../../../../presentation-components/settings-components/data-import/validation-results-step-card'
import { IHeadCell } from '../../../../../../shared/interfaces/table-display.model'
import FileUploadAllLineService from '../../../../../../shared/services/file-upload-all-lines/file-upload-all-lines.service'
import { IFileUploadLine, IReconciliationCandidate } from '../../../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model'

export const LinkReconciliationStep: FunctionComponent<{
  uploadResult: any
  typeOfData: string
  onHasReadyRowsChange?: (hasReadyRows: boolean) => void
}> = ({
  uploadResult,
  typeOfData,
  onHasReadyRowsChange,
}) => {
  const fileUploadAllLineService = useMemo(() => new FileUploadAllLineService(), [])
  const [rows, setRows] = useState<IFileUploadLine[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [inputPage, setInputPage] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [decisionLoadingId, setDecisionLoadingId] = useState<string | null>(null)
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false)
  const [candidateDialogLoading, setCandidateDialogLoading] = useState(false)
  const [candidateDialogSubmitting, setCandidateDialogSubmitting] = useState(false)
  const [selectedRow, setSelectedRow] = useState<IFileUploadLine | null>(null)
  const [candidates, setCandidates] = useState<IReconciliationCandidate[]>([])
  const [selectedCandidateId, setSelectedCandidateId] = useState('')

  const loadRows = useCallback(async () => {
    if (!uploadResult?.id || !typeOfData) return
    try {
      const resp = await fileUploadAllLineService.getUploadLinesWithPaginationByFile(typeOfData, uploadResult.id, 'reconciliation', inputPage, perPage)
      const nextRows = Array.isArray(resp.rows) ? resp.rows as IFileUploadLine[] : []
      setRows(nextRows)
      setTotalRecords(resp.count || 0)
      onHasReadyRowsChange?.(true)
    } catch {
      setRows([])
      setTotalRecords(0)
      onHasReadyRowsChange?.(false)
    }
  }, [fileUploadAllLineService, inputPage, onHasReadyRowsChange, perPage, typeOfData, uploadResult?.id])

  useEffect(() => {
    loadRows()
  }, [loadRows])

  const headCells: IHeadCell[] = [
    { id: 'match', numeric: false, label: 'Match', sortable: false },
    { id: 'action', numeric: false, label: 'Action', sortable: false },
    { id: 'conversion', numeric: false, label: 'Conversion', sortable: false },
    { id: 'unitsConsumed', numeric: true, label: 'Units Consumed', sortable: false },
    { id: 'startDate', numeric: false, label: 'Start Date', sortable: false },
    { id: 'endDate', numeric: false, label: 'End Date', sortable: false },
  ]

  const handleImportStandalone = async (row: IFileUploadLine) => {
    setDecisionLoadingId(row.id)
    try {
      await fileUploadAllLineService.editUploadLine({} as any, row.id, typeOfData, false, 'reconciliationDecision=standalone')
      await loadRows()
    } finally {
      setDecisionLoadingId(null)
    }
  }

  const handleOpenCandidateDialog = async (row: IFileUploadLine) => {
    setSelectedRow(row)
    setSelectedCandidateId(row.matchedEmissionId || '')
    setCandidates([])
    setCandidateDialogOpen(true)
    setCandidateDialogLoading(true)
    try {
      const resp = await fileUploadAllLineService.getUploadLineReconciliationCandidates(typeOfData, row.id)
      const nextCandidates = Array.isArray(resp?.reconciliationCandidates) ? resp.reconciliationCandidates : []
      setCandidates(nextCandidates)
      if (!row.matchedEmissionId && nextCandidates.length === 1) {
        setSelectedCandidateId(nextCandidates[0].id)
      }
    } finally {
      setCandidateDialogLoading(false)
    }
  }

  const handleCloseCandidateDialog = () => {
    setCandidateDialogOpen(false)
    setCandidateDialogLoading(false)
    setCandidateDialogSubmitting(false)
    setSelectedRow(null)
    setCandidates([])
    setSelectedCandidateId('')
  }

  const handleConfirmCandidate = async () => {
    if (!selectedRow || !selectedCandidateId) return
    setCandidateDialogSubmitting(true)
    try {
      await fileUploadAllLineService.editUploadLine({ matchedEmissionId: selectedCandidateId } as any, selectedRow.id, typeOfData, false, 'reconciliationDecision=link')
      handleCloseCandidateDialog()
      await loadRows()
    } finally {
      setCandidateDialogSubmitting(false)
    }
  }

  const tableRows = rows.map((row) => {
    const matchLabel = row.linkStatus === 'LINKED'
      ? 'Linked'
      : row.linkStatus === 'REVIEW'
        ? 'Needs review'
        : 'Standalone'
    const matchColor = row.linkStatus === 'LINKED'
      ? 'success'
      : row.linkStatus === 'REVIEW'
        ? 'warning'
        : 'default'
    const conversion = row.ruleId && row.rule
      ? `${row.ruleName}: ${[row.rule.groupName, row.rule.categoryName, row.rule.detailLevelTwo, row.rule.detailLevelThree, row.rule.detailLevelFour, row.rule.detailLevelFive, row.rule.measurementUnitName].filter(Boolean).join(', ')}`
      : [row.groupName, row.categoryName, row.detailLevelTwo, row.detailLevelThree, row.detailLevelFour, row.detailLevelFive, row.measurementUnitName].filter(Boolean).join(', ')

    return {
      id: row.id,
      match: (
        <Tooltip
          placement='top'
          title={(
            <Stack spacing={0.5}>
              {(row.matchReasons || []).map((reason, index) => (
                <div key={index}>{reason}</div>
              ))}
            </Stack>
          )}
        >
          <Chip size='small' color={matchColor as any} variant='outlined' label={matchLabel} />
        </Tooltip>
      ),
      action: (
        <Stack direction="row" spacing={1}>
          {row.linkStatus === 'LINKED' && (
            <>
              <Button size="small" variant="contained" disabled>
                Accept link
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={decisionLoadingId === row.id}
                onClick={() => handleImportStandalone(row)}
              >
                {decisionLoadingId === row.id ? 'Saving...' : 'Reject link'}
              </Button>
            </>
          )}
          {row.linkStatus === 'REVIEW' && (
            <>
              <Button
                size="small"
                variant="contained"
                disabled={decisionLoadingId === row.id}
                onClick={() => handleOpenCandidateDialog(row)}
              >
                Choose link
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={decisionLoadingId === row.id}
                onClick={() => handleImportStandalone(row)}
              >
                {decisionLoadingId === row.id ? 'Saving...' : 'Reject link'}
              </Button>
            </>
          )}
          {row.linkStatus === 'UNLINKED' && (
            <Button size="small" variant="outlined" disabled>
              Import standalone
            </Button>
          )}
        </Stack>
      ),
      conversion,
      unitsConsumed: row.unitsConsumed,
      startDate: row.startDate ? new Date(row.startDate).toLocaleDateString('en-GB') : '',
      endDate: row.endDate ? new Date(row.endDate).toLocaleDateString('en-GB') : '',
    }
  })

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Match links
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Review how valid rows will be handled. Linked rows will add activity calculations to existing spend-based emissions, standalone rows will create new activity emissions, and needs-review rows require confirmation.
      </Typography>
      {rows.some((row) => row.linkStatus === 'REVIEW') && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Review rows are optional. If you leave them unresolved, they will import as standalone activity emissions.
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'nowrap' }}>
        <ValidationResultsStepCard value={totalRecords} label="Rows to Review" color="text.primary" />
      </Box>
      <TableDisplay
        headCells={headCells}
        rows={tableRows}
        selected={[]}
        setSelected={() => { }}
        haveCheckboxSelect={false}
        selectByCheckboxOnly={false}
        selectByIcon={true}
        totalRecords={totalRecords}
        perPage={perPage}
        setPerPage={setPerPage}
        inputPage={inputPage}
        setInputPage={setInputPage}
        isMobileMode={false}
        isEmissionMobile={false}
        isReportActionCard={false}
      />
      <Dialog open={candidateDialogOpen} onClose={handleCloseCandidateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Choose Existing Emission</DialogTitle>
        <DialogContent>
          {candidateDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Select which existing spend-based emission this row should link to, or close this dialog and reject the link instead.
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Existing emission</InputLabel>
                <Select
                  label="Existing emission"
                  value={selectedCandidateId}
                  onChange={(event) => setSelectedCandidateId(event.target.value)}
                >
                  {candidates.map((candidate) => (
                    <MenuItem key={candidate.id} value={candidate.id}>
                      {candidate.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedCandidateId && (
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Match reasons
                  </Typography>
                  <Stack spacing={0.5}>
                    {(candidates.find((candidate) => candidate.id === selectedCandidateId)?.reasons || []).map((reason, index) => (
                      <Typography key={index} variant="body2">
                        {reason}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseCandidateDialog} disabled={candidateDialogSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCandidate}
            disabled={!selectedCandidateId || candidateDialogLoading || candidateDialogSubmitting}
          >
            {candidateDialogSubmitting ? 'Saving...' : 'Accept link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
